/**
 * The manual, read off disk.
 *
 * There is no list of chapters anywhere but the chapters themselves: each
 * `content/*.md` carries its own `order`, `part`, `title` and `lede` in
 * frontmatter, and the contents page, the rail, the pager and the search are
 * all derived from that. Adding a chapter is adding a file — which is the whole
 * reason the manual is markdown and not a component per page.
 *
 * `order` is the chapter number the reader sees, and the order of discovery:
 * install, find your way, run a task, check it, merge it, then everything that
 * scales it up. `part` is the heading a chapter sits under; the parts come in
 * the order of their first chapter, so a part's chapters must be numbered
 * together.
 */
export interface Doc {
  id: string;
  title: string;
  /** One line, for the crawler and the shared link. Never drawn on the page. */
  lede: string;
  order: number;
  /** The part of the manual it belongs to: the heading it is listed under. */
  part: string;
  /** `beta: true` in frontmatter: a BETA tag beside the title, in the rail and in the contents. */
  beta: boolean;
  html: string;
}

const FILES = import.meta.glob<{
  default: { meta: Record<string, string>; html: string };
}>('../../content/*.md', { eager: true });

export const DOCS: Doc[] = Object.entries(FILES)
  .map(([path, module]) => {
    const { meta, html } = module.default;
    return {
      id: path.replace(/^.*\//, '').replace(/\.md$/, ''),
      title: meta.title ?? 'Untitled',
      lede: meta.lede ?? '',
      order: Number(meta.order ?? 999),
      part: meta.part ?? 'More',
      beta: meta.beta === 'true',
      html
    };
  })
  .sort((a, b) => a.order - b.order);

export function findDoc(id: string): Doc | undefined {
  return DOCS.find((doc) => doc.id === id);
}

/** The contents page's search: it filters this list and nothing else. */
export function filterDocs(query: string): Doc[] {
  const q = query.trim().toLowerCase();
  if (!q) return DOCS;
  return DOCS.filter((doc) => doc.title.toLowerCase().includes(q) || doc.lede.toLowerCase().includes(q));
}

export interface Part {
  title: string;
  docs: Doc[];
}

/** `docs`, already in order, cut into its parts. A part with nothing left in `docs` is left out. */
export function partsOf(docs: Doc[]): Part[] {
  const parts: Part[] = [];
  for (const doc of docs) {
    const last = parts[parts.length - 1];
    if (last?.title === doc.part) last.docs.push(doc);
    else parts.push({ title: doc.part, docs: [doc] });
  }
  return parts;
}
